import React from 'react';
import { Link } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  HOMEPAGE_DRAFT_CATALOG,
  HOMEPAGE_DRAFT_HERO,
  HOMEPAGE_DRAFT_SOLUTIONS,
  HOMEPAGE_DRAFT_TASKS,
} from '../../data/homepageDraft';

const sectionClass = 'bg-white px-5 py-16 md:px-10 md:py-20 xl:px-[100px] xl:py-24';
const contentClass = 'mx-auto max-w-[1240px]';
const cardClass = 'flex h-full flex-col rounded-3xl bg-[#ecebf2] p-7 text-left shadow-[0_12px_12px_rgba(29,22,29,0.04)]';
const ctaClass = 'inline-flex h-[57px] items-center gap-3 rounded-2xl bg-[#F08517] px-7 text-base font-semibold text-white transition-colors hover:bg-[#d97412]';

export const HomepageDraftHero = ({ onOpen }) => (
  <section id="hero" className="scroll-mt-24 bg-white px-5 pb-12 pt-8 md:px-10 md:pb-16 md:pt-12 xl:px-[100px] xl:pb-20 xl:pt-16">
    <div className="mx-auto grid max-w-[1240px] items-center gap-8 md:grid-cols-2">
      <div>
        <h1 className="text-[32px] font-bold leading-[1.1] tracking-[-0.02em] text-[#0b0b0b] md:text-5xl xl:text-[56px]">
          {HOMEPAGE_DRAFT_HERO.titleStart}
          <br />
          {HOMEPAGE_DRAFT_HERO.titleEnd}
        </h1>
        <p className="mt-5 max-w-[520px] text-base leading-[1.4] text-[#2f2f2f]/80 md:text-lg">
          {HOMEPAGE_DRAFT_HERO.subtitle}
        </p>
        <button type="button" onClick={() => onOpen('hero')} className={`${ctaClass} mt-8`}>
          {HOMEPAGE_DRAFT_HERO.cta}
          <ArrowRight className="h-[18px] w-[18px]" aria-hidden />
        </button>
      </div>
      <div className="hidden min-h-[280px] md:block" aria-hidden />
    </div>
  </section>
);

export const HomepageDraftCatalog = () => (
  <section id="catalog" className={`${sectionClass} scroll-mt-24`}>
    <div className={`${contentClass} grid gap-6 md:grid-cols-3`}>
      {HOMEPAGE_DRAFT_CATALOG.map((item) => (
        <Link key={item.href} to={item.href} className={`${cardClass} transition-transform hover:-translate-y-0.5`}>
          <h2 className="text-xl font-bold leading-[1.15] text-[#0b0b0b] md:text-2xl">{item.title}</h2>
          <p className="mt-4 text-base leading-[1.4] text-[#2f2f2f]/80">{item.description}</p>
        </Link>
      ))}
    </div>
  </section>
);

export const HomepageDraftTasks = ({ onOpenTask }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: 'start', containScroll: 'trimSnaps' });

  return (
    <section id="tasks" className={`${sectionClass} scroll-mt-24 pt-0`}>
      <div className={contentClass}>
        <div className="mb-8 flex items-end justify-between gap-4">
          <h2 className="text-[32px] font-bold leading-none text-[#0b0b0b] md:text-4xl xl:text-[48px]">
            Для решения бизнес-задач
          </h2>
          <div className="hidden gap-2 md:flex">
            <button
              type="button"
              aria-label="Предыдущие задачи"
              onClick={() => emblaApi?.scrollPrev()}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-[#e5e7eb] bg-white"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Следующие задачи"
              onClick={() => emblaApi?.scrollNext()}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-[#e5e7eb] bg-white"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-6">
            {HOMEPAGE_DRAFT_TASKS.map((task) => (
              <div key={task} className="min-w-0 flex-[0_0_85%] sm:flex-[0_0_48%] xl:flex-[0_0_32%]">
                <button type="button" onClick={() => onOpenTask(task)} className={`${cardClass} w-full min-h-[140px]`}>
                  <span className="text-xl font-bold leading-[1.2] text-[#0b0b0b]">{task}</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export const HomepageDraftSolutions = ({ onOpenSolution }) => (
  <section id="solutions" className={`${sectionClass} scroll-mt-24 pt-0`}>
    <div className={contentClass}>
      <h2 className="text-[32px] font-bold leading-none text-[#0b0b0b] md:text-4xl xl:text-[48px]">
        Готовые решения для бизнеса
      </h2>
      <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        {HOMEPAGE_DRAFT_SOLUTIONS.map((name) => (
          <article key={name} className={cardClass}>
            <h3 className="text-xl font-bold leading-[1.2] text-[#0b0b0b]">{name}</h3>
            <button
              type="button"
              onClick={() => onOpenSolution(name)}
              className="mt-8 inline-flex h-[50px] items-center gap-2 self-start rounded-2xl bg-[#F08517] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#d97412]"
            >
              Подобрать решение
              <ArrowRight className="h-4 w-4" aria-hidden />
            </button>
          </article>
        ))}
      </div>
    </div>
  </section>
);
